import { useState } from "react";
import { ArrowLeft, BookOpen, Terminal, ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";
import { SEO } from "@/components/SEO";
import { PageHero, PageShell, Reveal } from "@/components/ui/page";
import { Band, SectionIcon } from "@/components/ui/lv";
import { guideArt } from "@/data/guideArt";
import { STACK_GUIDES } from "@/data/stackGuides";

const allGuides = [
  { slug: "monorepo", name: "Monorepo", frameworks: "Turborepo، pnpm workspace و چند سرویس از یک مخزن Git", path: "/guides/deploy-monorepo" },
  { slug: "python", name: "Python", frameworks: "Django، FastAPI و Flask", path: "/guides/deploy-python" },
  ...STACK_GUIDES.map((guide) => ({ ...guide, path: `/guides/deploy-${guide.slug}` })),
];

const FAQ = [
  {
    q: "دیپلوی چیست و چه فرقی با آپلود ساده فایل روی هاست دارد؟",
    a: "در بررسی اینکه دیپلوی چیست باید گفت دیپلوی (Deployment) فرآیند ساخت، تست، بسته‌بندی و اجرای خودکار یک برنامه نرم‌افزاری در یک محیط عملیاتی پایدار است. برخلاف آپلود دستی فایل با FTP که پر از خطای انسانی و زمان‌بر است، دیپلوی خودکار کانتینری وابستگی‌ها را نصب کرده و بدون توقف سرویس (Zero-Downtime) نسخه تازه را فعال می‌کند.",
  },
  {
    q: "مراحل اصلی دیپلوی پروژه در سکوی ابری گردو چگونه است؟",
    a: "برای دیپلوی پروژه در گردو کلاد سه گام اصلی طی می‌شود: ۱) اتصال مخزن گیت (GitHub یا GitLab) یا استفاده از ابزار CLI؛ ۲) تعیین متغیرهای محیطی و دستورات ساخت و اجرا؛ ۳) انتشار خودکار همراه با اختصاص زیردامنه، صدور خودکار گواهی SSL و هدایت ترافیک.",
  },
  {
    q: "استقرار نرم افزار روی سرویس PaaS چه مزایایی نسبت به سرور سنتی دارد؟",
    a: "در استقرار نرم افزار روی سکوی ابری، نیازی به نصب دستی وب‌سرور، پیکربندی فایروال، مدیریت گواهی SSL و نگرانی بابت به‌روزرسانی سیستم‌عامل ندارید. همچنین با مقیاس‌پذیری خودکار (Auto-scaling)، منابع همگام با ترافیک وب‌سایت تنظیم می‌شوند.",
  },
  {
    q: "آیا در این مرکز آموزش دیپلوی پروژه‌های چندسرویسی و میکروسرویس‌ها نیز پوشش داده شده‌اند؟",
    a: "بله، در کنار آموزش دیپلوی فریم‌ورک‌های مستقل، راهنمای جامع استقرار مونوریپو (Monorepo) برای مدیریت چندین سرویس، API و فرانت‌اند از یک مخزن واحد گیت با ابزارهایی مانند Turborepo و pnpm در دسترس شما قرار دارد.",
  },
];

export default function Guides() {
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  const schema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "آموزش دیپلوی روی گردو؛ همه‌ی زبان‌ها و فریم‌ورک‌ها",
    inLanguage: "fa-IR",
    url: "https://gerdoo.cloud/guides",
    description:
      "آموزش دیپلوی عملی Python، Node.js، Laravel، Go، Ruby، Rust، React، Next.js، Vue، Svelte، Astro و سایت استاتیک روی سکوی ابری گردو؛ از گیت تا دامنه و SSL.",
    hasPart: allGuides.map((guide) => ({
      "@type": "TechArticle",
      name: `دیپلوی ${guide.name} روی گردو`,
      url: `https://gerdoo.cloud${guide.path}`,
    })),
  };

  return (
    <PageShell className="lv-home selection:bg-gerdoo-500/20">
      <SEO
        title="آموزش دیپلوی پایتون، Node.js، لاراول و… | گردو کلاد"
        description="آموزش دیپلوی عملی Python، Node.js، Laravel، Go، Ruby، Rust، React، Next.js، Vue، Svelte، Astro و سایت استاتیک روی سکوی ابری گردو؛ از گیت تا دامنه و SSL."
        keywords="آموزش دیپلوی, دیپلوی چیست, دیپلوی پروژه, استقرار نرم افزار, استقرار نرم‌افزار, سکوی ابری گردو, دیپلوی ابری"
        canonicalPath="/guides"
        markdownPath="/guides.md"
        jsonLd={schema}
      />

      <PageHero
        badge="مرکز راهنماهای استقرار"
        badgeIcon={BookOpen}
        title="آموزش دیپلوی روی گردو؛ همه‌ی زبان‌ها و فریم‌ورک‌ها"
        art={guideArt("guides")}
        subtitle="آموزش دیپلوی عملی Python، Node.js، Laravel، Go، Ruby، Rust، React، Next.js، Vue، Svelte، Astro و سایت استاتیک روی سکوی ابری گردو؛ از گیت تا دامنه و SSL."
      />

      {/* Guide Cards Grid */}
      <Band className="pt-0 sm:pt-0">
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {allGuides.map((guide, i) => (
            <Reveal key={guide.path} i={i % 6} className="h-full">
              <Link to={guide.path} className="lv-card lv-card-hover group flex h-full flex-col p-6 text-start">
                <div className="flex items-start justify-between gap-3">
                  <SectionIcon icon={Terminal} />
                  <ArrowLeft
                    className="mt-1 h-4 w-4 shrink-0 text-ink-300 transition-all group-hover:-translate-x-1 group-hover:text-gerdoo-600"
                    aria-hidden
                  />
                </div>
                <h2 className="mt-5 text-[17px] font-bold leading-[1.7] tracking-tight text-ink-950">
                  دیپلوی {guide.name}
                </h2>
                <p className="mt-2 flex-1 text-[14.5px] leading-[1.95] text-ink-600">{guide.frameworks}</p>
              </Link>
            </Reveal>
          ))}
        </div>
      </Band>

      {/* Long-Form Educational Content */}
      <Band className="pt-0 sm:pt-0">
        <div className="mx-auto max-w-5xl">
          <div className="overflow-hidden rounded-tile bg-muted p-8 sm:p-12 text-start">
            <div className="prose prose-neutral prose-lg mx-auto max-w-none dark:prose-invert prose-headings:font-extrabold prose-headings:tracking-tight prose-headings:text-foreground prose-p:text-muted-foreground prose-p:leading-relaxed dark:prose-headings:text-foreground dark:prose-p:text-muted-foreground prose-li:text-muted-foreground dark:prose-li:text-muted-foreground prose-a:font-semibold prose-a:text-foreground prose-a:no-underline hover:prose-a:opacity-70">
              
              <h2 className="text-2xl sm:text-3xl font-extrabold">
                راهنمای جامع آموزش دیپلوی؛ از آماده‌سازی کد تا انتشار پایدار در بستر ابری
              </h2>

              <p className="leading-relaxed mt-4">
                یکی از مهم‌ترین مراحل در چرخه حیات توسعه نرم‌افزار، مرحله انتقال کدها از محیط محلی توسعه‌دهنده به سرور عملیاتی است. بسیاری از برنامه‌نویسان تازه کار می‌پرسند که مفهوم دیپلوی چیست و چه مراحلی برای انتشار یک برنامه در دسترس کاربران سراسر وب لازم است؟ در پاسخ باید گفت دیپلوی به فرآیند تبدیل کد منبع به یک سرویس در حال اجرا گفته می‌شود که به صورت پایدار و پیوسته پاسخگوی درخواست‌های کاربران باشد. با ورود سکوهای ابری مدرن (PaaS)، پیچیدگی‌های تنظیم دستی وب‌سرورها، فایروال‌ها و پایگاه‌های داده جای خود را به استقرار خودکار و بی‌دردسر داده‌اند.
              </p>

              <h3 className="text-xl sm:text-2xl font-bold mt-8 mb-4">
                مراحل کلیدی در فرآیند دیپلوی پروژه
              </h3>
              <p className="leading-relaxed">
                هنگامی که قصد انجام فرآیند دیپلوی پروژه را دارید، رعایت چند اصل بنیادین باعث می‌شود نرم‌افزار شما بدون وقفه و خطا به کار خود ادامه دهد:
              </p>
              <ul className="list-disc pr-6 space-y-2 mt-4">
                <li>
                  <strong>مدیریت وابستگی‌ها و بسته‌ها:</strong> قرار دادن فایل‌های استاندارد پکیج‌منیجر (نظیر package.json، requirements.txt، composer.json یا Cargo.toml) در ریشه مخزن به سکوی ابری امکان می‌دهد تا محیط اجرایی مناسب را به طور خودکار آماده کند.
                </li>
                <li>
                  <strong>جداسازی متغیرهای محیطی:</strong> کلیدهای امنیتی، اطلاعات اتصال به دیتابیس و آدرس‌های محرمانه نباید در کدها ثبت شوند؛ بلکه باید از طریق بخش متغیرهای محیطی به برنامه تزریق شوند.
                </li>
                <li>
                  <strong>تنظیم متغیر پورت (PORT):</strong> وب‌سرورهای مدرن باید پورت شنود خود را از متغیر محیطی سیستم‌عامل دریافت کنند و بر روی آدرس 0.0.0.0 تنظیم شوند تا ترافیک شبکه ابری بدون اختلال به سرویس هدایت گردد.
                </li>
              </ul>

              <h3 className="text-xl sm:text-2xl font-bold mt-8 mb-4">
                استقرار نرم افزار روی سکوی ابری در برابر هاست‌های سنتی
              </h3>
              <p className="leading-relaxed">
                در روش‌های کلاسیک، استقرار نرم افزار مستلزم ورود به کنترل‌پنل‌هایی نظیر cPanel یا لاگین به خط فرمان لینوکس با SSH و اجرای دستی دستورات وب‌سرور بود. این روش‌ها علاوه بر خطای انسانی بالا، ارتقای منابع را در ساعات اوج مصرف بسیار دشوار می‌کردند. در مقابل، با بهره‌گیری از <Link to="/products/paas">سرویس PaaS</Link> گردو کلاد، شما با هر push به مخزن گیت یا اجرای دستور در CLI، انتشار نسخه تازه را به شکلی کاملاً خودکار تجربه می‌کنید. همچنین برای آشنایی با جزئیات امکانات این بستر، مستندات <Link to="/support/docs/paas">آموزش paas</Link> در دسترس شماست.
              </p>

              {/* Comparison Table */}
              <div className="my-8 overflow-x-auto">
                <table className="w-full min-w-[600px] border-collapse text-sm">
                  <thead>
                    <tr className="border-b border-border/80 bg-background/50 text-foreground">
                      <th className="p-3 text-start font-bold">معیار استقرار</th>
                      <th className="p-3 text-start font-bold text-primary">سکوی ابری گردو کلاد</th>
                      <th className="p-3 text-start font-bold">سرور مجازی خام (VPS)</th>
                      <th className="p-3 text-start font-bold">هاست‌های سنتی (cPanel/DirectAdmin)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/60">
                    <tr>
                      <td className="p-3 font-semibold text-foreground">شیوه استقرار</td>
                      <td className="p-3 text-primary font-medium">اتصال خودکار به Git یا Gerdoo CLI</td>
                      <td className="p-3">دستورات دستی SSH یا تنظیم پیچیده CI/CD</td>
                      <td className="p-3">آپلود دستی فایل‌ها با FTP و فایل‌منیجر</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-semibold text-foreground">انتشار بدون قطعی (Zero-Downtime)</td>
                      <td className="p-3 text-primary font-medium">پشتیبانی کامل و پیش‌فرض</td>
                      <td className="p-3">نیازمند راه‌اندازی پیچیده رول‌اوت و پروکسی</td>
                      <td className="p-3">غیرممکن همراه با قطعی در زمان آپلود</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-semibold text-foreground">گواهی امنیتی SSL و دامنه‌ها</td>
                      <td className="p-3 text-primary font-medium">صدور و تمدید خودکار و رایگان</td>
                      <td className="p-3">نصب دستی Certbot و تمدید با کرون‌جاب</td>
                      <td className="p-3">تنظیم دستی در کنترل‌پنل</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-semibold text-foreground">مقیاس‌پذیری منابع</td>
                      <td className="p-3 text-primary font-medium">مقیاس خودکار هوشمند با پرداخت ساعتی</td>
                      <td className="p-3">ارتقای سخت‌افزاری همراه با ری‌استارت سرور</td>
                      <td className="p-3">خرید پلن‌های گران‌قیمت‌تر سالیانه</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3 className="text-xl sm:text-2xl font-bold mt-8 mb-4">
                دسته‌بندی راهنماهای استقرار برای استک‌های محبوب برنامه‌نویسی
              </h3>
              <p className="leading-relaxed">
                بسته به معماری فنی پروژه، راهنمای گام‌به‌گام مربوط به هر فریم‌ورک و زبان برای شما تدارک دیده شده است:
              </p>
              <ul className="list-disc pr-6 space-y-2 mt-4">
                <li>
                  <strong>زبان پایتون و هوش مصنوعی:</strong> برای استقرار فریم‌ورک‌های وب پایتون، راهنمای اختصاصی <Link to="/guides/deploy-python">دیپلوی جنگو</Link>، FastAPI و Flask را روی <Link to="/products/python">هاست پایتون ایران</Link> مطالعه کنید.
                </li>
                <li>
                  <strong>اکوسیستم جاوااسکریپت و تایپ‌اسکریپت:</strong> مستندات <Link to="/guides/deploy-nodejs">دیپلوی nodejs</Link> برای Express و NestJS در <Link to="/products/nodejs">هاست ابری nodejs</Link> و همچنین راهنمای <Link to="/guides/deploy-nextjs">دیپلوی nextjs</Link> با پشتیبانی از رندر سمت سرور در <Link to="/products/nextjs">هاست Next js</Link> فراهم شده است.
                </li>
                <li>
                  <strong>فرانت‌اند و سایت‌های استاتیک:</strong> جهت استقرار اپلیکیشن‌های تک‌صفحه‌ای راهنمای <Link to="/guides/deploy-react">دیپلوی react</Link> و برای میزبانی سریع فایل‌های HTML و CSS راهنمای <Link to="/guides/deploy-static-sites">دیپلوی سایت استاتیک</Link> در دسترستان قرار دارد.
                </li>
                <li>
                  <strong>فریم‌ورک‌های وب PHP و زبان‌های مدرن:</strong> راهنمای کاربردی <Link to="/guides/deploy-laravel">دیپلوی لاراول</Link> برای میزبانی وب‌سایت‌های سازمانی و مستندات <Link to="/guides/deploy-go">دیپلوی golang</Link> برای سرویس‌های پرسرعت کامپایلری در اختیار شماست.
                </li>
              </ul>

              <h3 className="text-xl sm:text-2xl font-bold mt-8 mb-4">
                استقرار نرم افزار در محیط عملیاتی با شبکه تحریم‌گذر
              </h3>
              <p className="leading-relaxed">
                یکی از موانع اصلی توسعه‌دهندگان ایرانی در زمان استقرار، محدودیت‌های ناشی از تحریم پکیج‌های توسعه است. گردو کلاد با شبکه تحریم‌گذر یکپارچه خود این مشکل را برای همیشه حل کرده است؛ بنابراین کلیه وابستگی‌های پایتون، نود، کامپوزر و راست با بیشترین سرعت ممکن دانلود شده و فرآیند بیلد کانتینر در زمانی کوتاه به اتمام می‌رسد.
              </p>
            </div>
          </div>
        </div>
      </Band>

      {/* FAQ Accordion Section */}
      <Band className="pt-0 sm:pt-0">
        <div className="mx-auto max-w-3xl text-start">
          <h2 className="mb-6 text-center text-2xl font-extrabold leading-[1.55] tracking-tight text-ink-950 sm:text-3xl sm:leading-[1.5]">
            سوالات متداول درباره آموزش دیپلوی
          </h2>
          <p className="mb-8 text-center text-ink-600">
            پاسخ به مهم‌ترین پرسش‌های توسعه‌دهندگان پیرامون مفاهیم استقرار، ابزارهای ابری و انتشار نرم‌افزار:
          </p>

          <div className="space-y-4">
            {FAQ.map((f, i) => {
              const isOpen = openFaqIndex === i;
              return (
                <Reveal key={f.q} i={i}>
                  <div className="overflow-hidden rounded-tile bg-muted transition-opacity">
                    <button
                      type="button"
                      onClick={() => setOpenFaqIndex(isOpen ? null : i)}
                      className="flex w-full items-center justify-between p-6 text-start font-bold text-ink-950 focus:outline-none"
                    >
                      <span className="text-base sm:text-lg">{f.q}</span>
                      <ChevronDown
                        className={`h-5 w-5 shrink-0 text-ink-400 transition-transform duration-200 ${
                          isOpen ? "rotate-180 text-ink-950" : ""
                        }`}
                      />
                    </button>
                    {isOpen && (
                      <div className="border-t border-black/[0.06] px-6 pb-6 pt-3">
                        <p className="text-sm leading-relaxed text-ink-600 sm:text-base">
                          {f.a}
                        </p>
                      </div>
                    )}
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </Band>
    </PageShell>
  );
}
